export interface CelestialStar {
  /** Radians clockwise from north. */
  az: number;
  /** Radians above the horizon. */
  alt: number;
  r: number;
  a: number;
  warm: boolean;
}

interface Vec3 {
  x: number;
  y: number;
  z: number;
}

function starDirection(az: number, alt: number): Vec3 {
  return {
    x: Math.sin(az) * Math.cos(alt),
    y: Math.cos(az) * Math.cos(alt),
    z: Math.sin(alt),
  };
}

function worldToViewDir(dir: Vec3, pitchDeg: number, bearingDeg: number): Vec3 {
  const pitch = (pitchDeg * Math.PI) / 180;
  const bearing = (bearingDeg * Math.PI) / 180;

  // Inverse of the map's horizontal spin so stars stay fixed in the world.
  const cosB = Math.cos(bearing);
  const sinB = Math.sin(bearing);
  const x1 = dir.x * cosB - dir.y * sinB;
  const y1 = dir.x * sinB + dir.y * cosB;
  const z1 = dir.z;

  const cosP = Math.cos(pitch);
  const sinP = Math.sin(pitch);
  return {
    x: x1,
    y: z1 * sinP + y1 * cosP,
    z: z1 * cosP - y1 * sinP,
  };
}

/** Deterministic stars on the celestial sphere (fixed to world north). */
export function buildCelestialStarfield(count: number, seed = 42): CelestialStar[] {
  let s = seed;
  const rand = () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };

  const stars: CelestialStar[] = [];
  for (let i = 0; i < count; i++) {
    const az = rand() * Math.PI * 2;
    const u = rand();
    const alt = (0.04 + u * u * 0.86) * (Math.PI / 2);
    stars.push({
      az,
      alt,
      r: rand() < 0.025 ? 1.6 : rand() < 0.12 ? 1.05 : 0.55,
      a: 0.35 + rand() * 0.65,
      warm: rand() < 0.18,
    });
  }
  return stars;
}

function paintNebula(ctx: CanvasRenderingContext2D, width: number, height: number, pitchT: number) {
  const haze = pitchT * 0.85;
  const nebula = ctx.createRadialGradient(width * 0.72, height * 0.2, 0, width * 0.72, height * 0.2, width * 0.5);
  nebula.addColorStop(0, `rgba(56, 88, 168, ${0.16 * haze})`);
  nebula.addColorStop(1, 'rgba(56, 88, 168, 0)');
  ctx.fillStyle = nebula;
  ctx.fillRect(0, 0, width, height);

  const nebula2 = ctx.createRadialGradient(width * 0.22, height * 0.34, 0, width * 0.22, height * 0.34, width * 0.38);
  nebula2.addColorStop(0, `rgba(88, 48, 120, ${0.11 * haze})`);
  nebula2.addColorStop(1, 'rgba(88, 48, 120, 0)');
  ctx.fillStyle = nebula2;
  ctx.fillRect(0, 0, width, height);
}

export function paintCelestialStarfield(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  stars: CelestialStar[],
  pitchDeg: number,
  bearingDeg: number,
  pitchT: number,
) {
  ctx.clearRect(0, 0, width, height);
  if (pitchT <= 0) return;

  paintNebula(ctx, width, height, pitchT);

  const fov = 0.95 + (pitchDeg / 90) * 0.35;

  for (const star of stars) {
    const dir = starDirection(star.az, star.alt);
    const view = worldToViewDir(dir, pitchDeg, bearingDeg);
    const depth = -view.z;
    if (depth <= 0.04) continue;

    const sx = view.x / depth;
    const sy = view.y / depth;
    const x = width * (0.5 + sx * fov);
    const y = height * (0.5 - sy * fov);

    if (x < -24 || x > width + 24 || y < -24 || y > height + 24) continue;

    const horizonFade = Math.min(1, Math.max(0, (dir.z - 0.03) / 0.12));
    const alpha = star.a * horizonFade * pitchT;
    if (alpha <= 0.02) continue;

    ctx.beginPath();
    ctx.arc(x, y, star.r, 0, Math.PI * 2);
    ctx.fillStyle = star.warm
      ? `rgba(255, 236, 210, ${alpha})`
      : `rgba(226, 236, 255, ${alpha})`;
    ctx.fill();
  }
}

export function skyPitchBlend(pitch: number, maxPitch: number, topDownPitch: number) {
  return Math.max(0, Math.min(1, (pitch - topDownPitch) / Math.max(1, maxPitch - topDownPitch)));
}

/** Soft horizontal fade so stars blend into the map horizon/fog. */
export function skyHorizonMask(pitchT: number) {
  const skyEnd = 8 + pitchT * 52;
  const fadeEnd = skyEnd + 10 + pitchT * 6;
  return `linear-gradient(to bottom, black 0%, black ${skyEnd}%, transparent ${fadeEnd}%)`;
}
